import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { useMobileSidebar } from '@/contexts/MobileSidebar/MobileSidebarContext';
import useFundIdFromPath from '@/hooks/useFundIdFromPath';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import Logo1 from '@/assets/images/logo1.svg';
import ReminderPerson from '@/assets/icons/reminderPerson.svg';
import arrowDown from '@/assets/icons/arrowDown.svg';
import LogoutIcon from '@/assets/icons/logout.svg';
import styles from './Header.module.scss';
import HeaderItem from './HeaderItem';
import Button from '@/components/ui/Button';
import Dropdown from '@/components/ui/Dropdown';
import InquiryQueueIndicator from '@/components/Inquiries/InquiryQueueIndicator';
import UpdatesBell from '@/components/SystemMessages/UpdatesBell';
import { setPreferOldSiteUntilEndOfDay } from '@/helpers/preferOldSite';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import { buildOldSiteUrl } from '@/helpers/sitePathMap';
import OldSiteExitDialog from '@/components/adoption/OldSiteExitDialog';

const OLD_WEBSITE_BASE_URL = import.meta.env.VITE_OLD_WEBSITE_URL;

function Header() {
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuthContext();
  const { isMobile } = useDeviceDetection();
  const { isMobileSidebarOpen, toggleMobileSidebar } = useMobileSidebar();
  const navigate = useNavigate();
  const { captureEvent } = usePostHogIdentification();
  const [exitSurveyOpen, setExitSurveyOpen] = useState(false);
  /* "החשבון שלי" קיים בשתי כתובות, ולא בגלל חוסר החלטה: מתחת ל-:fundId המסך מקבל
     את הסיידבר כמו כל מסך אחר, אבל הכתובת ההיא דורשת קרן. לעובדי משרד fundCodes
     הוא [] - ולכן הפריט נעלם להם לגמרי, בלי שום דרך לשנות שם או להחליף סיסמה.
     החשבון אינו שייך לקרן, אז כשאין קרן הולכים לכתובת שאינה דורשת אותה.

     fundCodes ולא fundIds: שלוש התשובות שיוצרות סשן (התחברות, התחברות בקוד קולי,
     והתחברות-בשם) מחזירות FundCodes, ורק users/get מחזיר FundIds. */
  const fundIdFromPath = useFundIdFromPath();
  const ownFunds = currentUser?.fundCodes ?? currentUser?.fundIds;
  const settingsFundId = fundIdFromPath ?? ownFunds?.[0]?.toString() ?? null;

  /* הדגל נקרא כאן מ-GlobalFeatureFlagsProvider, שיושב מעל ה-router ולכן חל גם על
     הכותרת. הפריט מוסתר לגמרי כשהדגל כבוי, ולא מוצג מנוטרל: מסך שלא שוחרר אינו
     הבטחה לעתיד שכדאי לפרסם בתפריט - אותו שיקול כמו ב-hideWhenFeatureOff בסיידבר. */
  const isMyAccountOn = useFeature('my-account');

  const goToOldSite = () => {
    captureEvent('clicked_to_old_site', {
      from_path: window.location.pathname,
    });
    setPreferOldSiteUntilEndOfDay();
    const url = OLD_WEBSITE_BASE_URL ? buildOldSiteUrl(OLD_WEBSITE_BASE_URL, window.location.pathname) : '';
    if (url) {
      window.location.assign(url);
    }
  };

  const handleOldSiteClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setExitSurveyOpen(true);
  };

  const handleExitProceed = () => {
    setExitSurveyOpen(false);
    goToOldSite();
  };

  // יש קרן - הנתיב שמתחתיה, כדי שהמסך יקבל את הסיידבר. אין - הכתובת שאינה דורשת קרן.
  const goToUserSettings = () => navigate(settingsFundId ? `/${settingsFundId}/user-settings` : '/user-settings');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLanguageChange = (newLanguage: 'he' | 'en') => {
    i18n.changeLanguage(newLanguage);
  };

  const languageChoices = (
    <>
      <Button onClick={() => handleLanguageChange('en')} disabled={i18n.language === 'en'}>
        {'English'}
      </Button>
      <Button onClick={() => handleLanguageChange('he')} disabled={i18n.language === 'he'}>
        {'עברית'}
      </Button>
    </>
  );

  return (
    <header className={styles.headerContainer}>
      <div className={styles.headerStart}>
        {currentUser && (
          <button className={styles.mobileHamburger} onClick={toggleMobileSidebar} aria-label={isMobileSidebarOpen ? t('closeSidebar') : t('openSidebar')}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        )}
        {/* HAT-1248: באתר הישן לחיצה על הלוגו טענה את הדף מחדש, והמשתמשים התרגלו לזה כדי
            לראות נתונים מעודכנים. טעינה מלאה של אותה כתובת, כמו F5: הסשן נשמר, והמסך
            נשאר במקום שבו המשתמש עומד ולא קופץ לדף הבית. */}
        <button type="button" className={styles.logoButton} onClick={() => window.location.reload()} title={t('refreshPage')} aria-label={t('refreshPage')}>
          <img src={Logo1} alt={t(`siteTitle.${import.meta.env.VITE_APP_SITE_ID}`)} className={styles.logo} />
        </button>
      </div>
      <nav className={styles.navigationWrapper}>
        {/* A phone header has room for the burger, the logo, the inquiries badge
            and the way back to the old site. Language moves in with the account
            menu rather than competing for that width. */}
        {!isMobile && (
          <Dropdown
            trigger={<HeaderItem text={t('language')} icon={arrowDown} className="text-[var(--font-body-3)]" />}
            contentAlign="start"
            closeOnContentClick
          >
            <div className={styles.dropdown}>{languageChoices}</div>
          </Dropdown>
        )}
        {currentUser && (
          <>
            {/* עדכוני המערכת, במקום שאפשר לחזור אליהם - ולא רק בפופ-אפ החד-פעמי של הכניסה */}
            <UpdatesBell />
            {/* מספר הפניות שמחכות למשרד, וכניסה אליהן מכל מסך באתר */}
            <InquiryQueueIndicator />
            <div className={styles.divider} />
            <Dropdown
              trigger={
                <HeaderItem text={`${currentUser.firstName} ${currentUser.lastName}`} icon={ReminderPerson} className="text-[var(--font-body-1)] ph-mask" />
              }
              contentAlign="start"
              closeOnContentClick
            >
              <div className={styles.dropdown}>
                {isMobile && (
                  <>
                    {languageChoices}
                    <div className={styles.dropdownSeparator} />
                    {/* HAT-1170: במסך צר הכפתור הבורדו "לאתר הישן" יורד
                        מהכותרת אל תפריט החשבון — הפעולה נשארת, המקום משתנה.
                        בעיצוב שממנו מעבירים הכותרת נושאת לוגו וצ'יפ משתמש
                        בלבד, וכפתור מלא לצידם הוא הדבר הראשון שהעין נופלת
                        עליו במקום השם והיתרה. */}
                    {OLD_WEBSITE_BASE_URL && <Button onClick={() => setExitSurveyOpen(true)}>{t('toOldWebsite')}</Button>}
                  </>
                )}
                {/* הכניסה היחידה ל"החשבון שלי" (HAT-1127): המדווח ביקש "מקום ברור",
                    וזה המקום שבו מחפשים אותו - התפריט שנושא את שם המשתמש.
                    המפריד תלוי בפריט: בלעדיו הוא נשאר קו בודד בראש התפריט למי
                    שהפריט מוסתר אצלו, ושני קווים צמודים בנייד. */}
                {isMyAccountOn && (
                  <>
                    <Button onClick={goToUserSettings}>{t('userSettingsPage.title')}</Button>
                    {isMobile && <div className={styles.dropdownSeparator} />}
                  </>
                )}
                {/* HAT-1445: במסך רחב "התנתק" יוצא מהתפריט אל הכותרת (ראו מטה). בנייד אין שם
                    מקום, ולכן הוא נשאר כאן. */}
                {isMobile && <Button onClick={handleLogout}>{t('logout')}</Button>}
              </div>
            </Dropdown>
            {/* HAT-1445: מי שיושב על מחשב ציבורי חייב למצוא איך לצאת בלי לנחש שהשם הוא תפריט.
                כפתור גלוי, לא פריט בתפריט הנפתח. */}
            {!isMobile && (
              <button type="button" className={styles.logoutButton} onClick={handleLogout}>
                <img src={LogoutIcon} alt="" />
                <span className="body-md-regular">{t('logout')}</span>
              </button>
            )}
          </>
        )}
        {/* למי שמחובר במסך צר הקישור יושב בתפריט החשבון (למעלה); למי שלא
            מחובר אין תפריט כזה, ולכן הקישור נשאר גלוי. */}
        {OLD_WEBSITE_BASE_URL && !(isMobile && currentUser) && (
          <a
            className={styles.oldSiteLink}
            href={buildOldSiteUrl(OLD_WEBSITE_BASE_URL, typeof window !== 'undefined' ? window.location.pathname : '/')}
            onClick={handleOldSiteClick}
          >
            <span className="body-sm-semibold">{t('toOldWebsite')}</span>
          </a>
        )}
      </nav>
      <OldSiteExitDialog open={exitSurveyOpen} onProceed={handleExitProceed} />
    </header>
  );
}

export default Header;
